import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { Store, select } from '@ngrx/store';
import { Observable, forkJoin, of, switchMap, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, filter, finalize, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { Mapper, TiimePipesModule } from 'tiime-expert-utils';
import {
  DialogBase,
  TiimeAlertModule,
  TiimeButtonModule,
  TiimeDialogModule,
  TiimeIconModule,
  TiimeMenuModule,
  TiimeSnackbarService,
  TiimeTooltipModule
} from 'tiime-material';

import { BusinessUserApiService } from '@api-services/business-user-api.service';
import { TimeTrackingsApiService } from '@api-services/time-trackings-api.service';
import { PreventMultipleClick } from '@decorators/prevent-multiple-click';
import { SendContractType } from '@enums/send-contract-type.enum';
import { GICachedDataApiService } from '@gi/api/services/gi-cached-data-api.service';
import { ContractAnnexLineCharge } from '@gi/enums/contract-annex-line-charge.enum';
import { ContractStatus } from '@gi/enums/contract-status.enum';
import { ProspectAcquisitionChannel } from '@gi/enums/prospect-acquisition-channel.enum';
import { ContractForm } from '@gi/forms/contract-form';
import { Contract } from '@gi/models/contract';
import { ContractAnnexModel } from '@gi/models/contract-annex-model';
import { DateHelper, DateInput } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { AppStoreState } from '@interfaces/app-store-state';
import { BusinessUnit } from '@models/business-unit';
import { BusinessUser } from '@models/business-user';
import { CodeAndLabel } from '@models/code-and-label';
import { GIFile } from '@models/gi-file';
import { ModelOffer } from '@models/model-offer';
import { Partner } from '@models/partner';
import { TimeTrackingsTask } from '@models/time-trackings-task';
import { ContractFacade } from '@modules/core/facades/contract.facade';
import {
  businessUserPartnerSelector,
  businessUserSelector
} from '@modules/core/store/business-user/business-user-selector';
import { ContractRemindersButtonComponent } from '@shared-components/contract-reminders-button/contract-reminders-button.component';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { ConfirmDialogService } from '@shared-dialogs/confirm-dialog/confirm-dialog.service';
import { ReturnedUpdateContractStatusDialogData } from '@shared-dialogs/update-contract-status-dialog/returned-update-contract-status-dialog-data';
import { ContractFormComponent } from '@shared-forms/contract-form/contract-form.component';
import { AtByPipe } from '@shared-pipes/at-by/at-by.pipe';
import { CodeAndLabelPipe } from '@shared-pipes/code-and-label/code-and-label.pipe';
import { ContractStatusSelectComponent } from '@shared-selectors/contract-status-select/contract-status-select.component';

import { ContractContactContainerComponent } from './contract-contact-container/contract-contact-container.component';
import { ContractProspectContainerComponent } from './contract-prospect-container/contract-prospect-container.component';
import { CreateOrUpdateContractDialogData } from './create-or-update-contract-dialog-data';

@Component({
  selector: 'app-create-or-update-contract-dialog',
  standalone: true,
  imports: [
    CommonModule,
    TiimeIconModule,
    TiimeDialogModule,
    TiimeButtonModule,
    TiimeTooltipModule,
    TiimeMenuModule,
    ContractFormComponent,
    TiimePipesModule,
    ContractContactContainerComponent,
    ContractProspectContainerComponent,
    ContractStatusSelectComponent,
    CodeAndLabelPipe,
    AtByPipe,
    ContractRemindersButtonComponent,
    TiimeAlertModule
  ],
  templateUrl: './create-or-update-contract-dialog.component.html',
  styleUrls: ['./create-or-update-contract-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateOrUpdateContractDialogComponent extends DialogBase<CreateOrUpdateContractDialogComponent> {
  savingContract = false;

  readonly contractForm: ContractForm = new ContractForm();
  readonly contractLossReasons$ = this.gICachedDataApiService.getContractLossReasons();
  readonly tasks$: Observable<TimeTrackingsTask[]> = this.timeTrackingsApiService.getTasks({ prestation: true });
  readonly modelOffers$: Observable<ModelOffer[]> = this.contractFacade.getModelOffers();
  readonly annexModels$: Observable<ContractAnnexModel[]> = this.contractFacade.getAnnexModels();
  readonly partner$: Observable<Partner> = this.store.pipe(select(businessUserPartnerSelector));
  readonly acquisitionChannels: Signal<CodeAndLabel<ProspectAcquisitionChannel>[]> = toSignal(
    this.gICachedDataApiService.getProspectAcquisitionChannels()
  );
  readonly contractAnnexLineTypes: Signal<CodeAndLabel<string>[]> = toSignal(
    this.gICachedDataApiService.getContractAnnexLineTypes()
  );
  readonly contractAnnexLineCharges: Signal<CodeAndLabel<ContractAnnexLineCharge>[]> = toSignal(
    this.gICachedDataApiService.getContractAnnexLineCharges()
  );

  readonly businessUnits$: Observable<BusinessUnit[]> =
    this.businessUserApiService.getBusinessUserPartnerBusinessUnits();
  readonly ContractStatus = ContractStatus;
  readonly SendContractType = SendContractType;

  private readonly sendContractContactRequiredControls: AbstractControl[] = [
    this.contractForm.businessUnit,
    this.contractForm.company,
    this.contractForm.startDate,
    this.contractForm.prestations,
    this.contractForm.contact,
    this.contractForm.owner
  ];
  private readonly sendContractProspectRequiredControls: AbstractControl[] = [
    this.contractForm.businessUnit,
    this.contractForm.startDate,
    this.contractForm.prestations,
    this.contractForm.prospect,
    this.contractForm.owner
  ];
  private shouldForceCloseWithContract: boolean;

  readonly mapToArchiveTooltipMessage: Mapper<ContractStatus, string> = (status: ContractStatus) =>
    status !== ContractStatus.sent ? 'Archiver le contrat' : `Impossible d'archiver un contrat envoyé`;

  constructor(
    dialogRef: MatDialogRef<CreateOrUpdateContractDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: CreateOrUpdateContractDialogData,
    private readonly snackbarService: TiimeSnackbarService,
    private readonly timeTrackingsApiService: TimeTrackingsApiService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly store: Store<AppStoreState>,
    private readonly gICachedDataApiService: GICachedDataApiService,
    private readonly businessUserApiService: BusinessUserApiService,
    private readonly contractFacade: ContractFacade
  ) {
    super(dialogRef);

    this.initContractForm(data.contract);
    this.manageDisabledFields();
  }

  submitContract(): void {
    this.removeSendContractValidators();

    if (this.contractForm.invalid) {
      this.contractForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.contractForm.disable();

    this.saveContract()
      .pipe(
        tap((contract: Contract) => this.dialogRef.close(contract)),
        finalize(() => {
          this.isLoading.set(false);
          this.manageDisabledFields();
        })
      )
      .subscribe();
  }

  addReminder(external: boolean): void {
    if (external) {
      this.addExternalReminder();
    } else {
      this.sendContract(SendContractType.reminder);
    }
  }

  updateNextReminderDate(nextReminderDate: DateInput): void {
    this.contractFacade
      .getBusinessUser()
      .pipe(
        tap((businessUser: BusinessUser) =>
          this.contractForm.patchValue({
            nextReminderDate: DateHelper.format(nextReminderDate),
            nextReminderDateModifiedAt: new Date(),
            nextReminderDateModifiedBy: businessUser
          })
        )
      )
      .subscribe();
  }

  @PreventMultipleClick('savingContract')
  sendContract(type: SendContractType): void {
    this.savingContract = true;

    if (this.contractForm.archivedAt.value) {
      this.savingContract = false;
      return;
    }

    this.addSendContractValidators();

    if (this.contractForm.invalid) {
      this.contractForm.markAllAsTouched();
      this.savingContract = false;
      return;
    }

    this.isLoading.set(true);
    this.contractForm.disable();

    this.saveContract()
      .pipe(
        tap((contract: Contract) => this.contractForm.fromContract(contract)),
        finalize(() => {
          this.isLoading.set(false);
          this.savingContract = false;
          this.manageDisabledFields();
          this.removeSendContractValidators();
        }),
        withLatestFrom(this.partner$),
        switchMap(([contract, partner]: [Contract, Partner]) =>
          this.contractFacade.openSendContractDialog(contract, type, partner.id)
        ),
        filter((confirm: boolean) => confirm),
        switchMap(() => this.getBusinessUser()),
        tap((businessUser: BusinessUser) => {
          const contract = this.contractForm.toContract();
          contract.status = ContractStatus.sent;
          contract.statusAt = new Date();
          contract.statusBy = businessUser;
          contract.sentAt = contract.statusAt;
          contract.sentBy = contract.statusBy;
          contract.nextReminderDate = DateHelper.format(DateHelper.add(Date.now(), 1, 'day'));

          if (type === SendContractType.reminder) {
            contract.reminderCount += 1;
          }

          this.close(contract);
        })
      )
      .subscribe();
  }

  setContractArchived(archived: boolean): void {
    this.confirmDialogService
      .open({
        data: new ConfirmDialogData(
          'Attention',
          `Êtes-vous sûr de vouloir ${archived ? '' : 'dés'}archiver ce contrat ?`,
          'Confirmer',
          'Annuler',
          null,
          'accent'
        )
      })
      .afterClosed()
      .pipe(
        filter((confirm: boolean) => confirm),
        tap(() => {
          this.isLoading.set(true);
          this.contractForm.disable();
        }),
        switchMap(() => this.contractFacade.archiveContract(this.contractForm.id.value, archived)),
        tap((contract: Contract) => {
          this.contractForm.patchValue({ archivedAt: contract.archivedAt, archivedBy: contract.archivedBy });
          this.shouldForceCloseWithContract = true;
          this.snackbarService.openSuccess(`Le contrat a bien été ${archived ? '' : 'dés'}archivé`);
        }),
        finalize(() => {
          this.isLoading.set(false);
          this.manageDisabledFields();
        })
      )
      .subscribe();
  }

  changeContractStatus(nextStatus: ContractStatus): void {
    if (this.contractForm.archivedAt.value) {
      return;
    }

    const currentStatus = this.contractForm.contractStatus.value;
    if (currentStatus === nextStatus) {
      return;
    }

    this.removeSendContractValidators();

    if (this.contractForm.invalid) {
      this.contractForm.markAllAsTouched();
      return;
    }

    this.contractFacade
      .openConfirmUpdateContractStatus(currentStatus, nextStatus, true)
      .pipe(
        filter((data: ReturnedUpdateContractStatusDialogData) => !!data),
        tap(() => {
          this.isLoading.set(true);
          this.contractForm.contractStatus.setValue(nextStatus);
          this.contractForm.disable();
        }),
        switchMap((data: ReturnedUpdateContractStatusDialogData) =>
          this.saveContract().pipe(map((contract: Contract) => ({ data, contract })))
        ),
        tap(({ contract }: { contract: Contract }) => {
          this.contractForm.fromContract({ ...contract, status: nextStatus });
          this.shouldForceCloseWithContract = true;
        }),
        switchMap(({ data, contract }: { data: ReturnedUpdateContractStatusDialogData; contract: Contract }) =>
          this.contractFacade.updateContractStatus(contract.id, nextStatus, data.lossReason, data.lossComment)
        ),
        tap((contract: Contract) => {
          this.isLoading.set(false);
          this.contractForm.fromContract(contract);
          this.shouldForceCloseWithContract = true;
          this.manageDisabledFields();
        }),
        catchError(err => {
          this.isLoading.set(false);
          this.contractForm.contractStatus.setValue(currentStatus);
          this.manageDisabledFields();

          return throwError(() => err);
        })
      )
      .subscribe();
  }

  downloadContractFile(fileId: number): void {
    this.isLoading.set(true);

    this.contractFacade
      .downloadContractFile(this.contractForm.id.value, fileId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe();
  }

  openContractFileInNewTab(fileId: number): void {
    this.isLoading.set(true);

    this.contractFacade
      .openContractFileInNewTab(this.contractForm.id.value, fileId)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe();
  }

  override cancel(): void {
    if (this.shouldForceCloseWithContract && this.contractForm.id.value) {
      this.contractFacade
        .getContract(this.contractForm.id.value)
        .pipe(tap((contract: Contract) => super.close(contract)))
        .subscribe();
    } else {
      super.cancel();
    }
  }

  private initContractForm(contract: Contract): void {
    this.contractForm.fromContract(contract);
  }

  private saveContract(): Observable<Contract> {
    const contract = this.contractForm.toContract();
    const filesToUpload = this.contractForm.getFilesToUpload();
    const filesToDeleteIds = this.contractForm.getFilesToDelete();

    const contractObs = contract.id
      ? this.contractFacade.updateContract(contract)
      : this.contractFacade.createContract(contract);

    return contractObs.pipe(
      switchMap((savedContract: Contract) =>
        forkJoin(
          [of(savedContract)].concat(
            filesToDeleteIds
              .map(fileToDeleteId =>
                this.contractFacade.deleteContractFile(savedContract.id, fileToDeleteId).pipe(
                  map(() => fileToDeleteId),
                  catchError(err => of(err))
                )
              )
              .concat(
                filesToUpload.map(fileToUpload =>
                  this.contractFacade
                    .addContractFile(savedContract.id, fileToUpload.content, fileToUpload.title)
                    .pipe(catchError(err => of(err)))
                )
              )
          )
        )
      ),
      defaultIfEmpty([]),
      tap(() => this.snackbarService.openSuccess(`Le contrat a bien été ${contract.id ? 'modifié' : 'ajouté'}`)),
      map((datas: (Contract | number | GIFile)[]) => {
        const savedContract: Contract = datas.find(data => data instanceof Contract) as Contract;
        const deletedFiles: number[] = datas.filter(data => typeof data === 'number') as number[];
        const uploadedFiles: GIFile[] = datas.filter(data => data instanceof GIFile) as GIFile[];

        deletedFiles.forEach((fileId: number) => {
          const index = savedContract.files.findIndex((file: GIFile) => file.id === fileId);
          if (index !== -1) {
            savedContract.files.splice(index, 1);
          }
        });

        uploadedFiles.forEach((file: GIFile) => savedContract.files.push(file));

        return savedContract;
      })
    );
  }

  private addExternalReminder(): void {
    this.contractFacade
      .openSendExernalReminderConfirmDialog()
      .pipe(
        filter((confirm: boolean) => confirm),
        tap(() => this.isLoading.set(true)),
        switchMap(() => this.contractFacade.sendExternalReminder(this.contractForm.id.value)),
        tap(() => {
          this.contractForm.incrementReminderCount();
          this.shouldForceCloseWithContract = true;
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  private getSendContractRequiredControls(): AbstractControl[] {
    return this.contractForm.company.value
      ? this.sendContractContactRequiredControls
      : this.sendContractProspectRequiredControls;
  }

  private addSendContractValidators(): void {
    this.getSendContractRequiredControls().forEach((control: AbstractControl) => {
      control.setValidators(Validators.required);
      control.updateValueAndValidity();
    });

    if (
      this.contractForm.company.value &&
      this.contractForm.contact.value &&
      (!this.contractForm.contact.value.phone || !this.contractForm.contact.value.email)
    ) {
      FormHelper.addErrorToControl(this.contractForm.contact, 'incompleteContact');
    }
  }

  private removeSendContractValidators(): void {
    this.getSendContractRequiredControls().forEach((control: AbstractControl) => {
      control.clearValidators();
      control.updateValueAndValidity();
    });

    if (this.contractForm.company.value) {
      FormHelper.removeErrorFromControl(this.contractForm.contact, 'incompleteContact');
    }
  }

  private manageDisabledFields(): void {
    this.contractForm.contractStatus.enable();

    if (this.contractForm.archivedAt.value) {
      this.contractForm.disable();
      return;
    }

    if (this.contractForm.contractStatus.value === ContractStatus.draft) {
      this.contractForm.enable();
      this.contractForm.disableTasksFromOffers();
      this.contractForm.disableTypeAndLabelFromAnnexLines();
      return;
    }

    this.contractForm.disable();
    this.contractForm.startDate.enable();
    this.contractForm.internalComment.enable();
  }

  private getBusinessUser(): Observable<BusinessUser> {
    return this.store.pipe(select(businessUserSelector), take(1));
  }
}
